import React, { useState, useCallback, useEffect } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import { Box, Drawer } from '@mui/material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import CalendarEvent, { ICalendarEvent } from './CalendarEvent';
import * as actions from '../../../../store/actions';
import { i18n } from "../../../../translate/i18n";
import { RootState } from '../../../../store/types';
import Spinner from '../../../../components/Spinner';
import ScheduledServicesByDayAndCompletedModal from '../Modals/ScheduledServicesByDayAndCompletedModal';

const localizer = momentLocalizer(moment);

const messages = {
  allDay: i18n.t('scheduledServicesCalendarCalendar.allDay'),
  previous: '<',
  next: '>',
  today: i18n.t('scheduledServicesCalendarCalendar.today'),
  month: i18n.t('scheduledServicesCalendarCalendar.month'),
  week: i18n.t('scheduledServicesCalendarCalendar.week'),
  day: i18n.t('scheduledServicesCalendarCalendar.day'),
  agenda: i18n.t('scheduledServicesCalendarCalendar.agenda'),
  date: i18n.t('scheduledServicesCalendarCalendar.date'),
  time: i18n.t('scheduledServicesCalendarCalendar.time'),
  event: i18n.t('scheduledServicesCalendarCalendar.event'),
  showMore: (total: number)=> `${i18n.t('scheduledServicesCalendarCalendar.showMore')} (${total})`,
};

type Props = {
  redirect: CallableFunction,
  calendarDate: Date,
  setCalendarDate: React.Dispatch<React.SetStateAction<Date>>,
}

const ScheduledServicesCalendarCalendar: React.FC<Props> = ({ redirect, calendarDate, setCalendarDate }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState<View>('month');
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const calendarEvents = useSelector((state: RootState) => state.scheduledServices.calendar.calendarEvents);
  const loading = useSelector((state: RootState) => state.scheduledServices.calendar.loadingCalendarEvents);
  const filteredSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const filters = useSelector((state: RootState) => state.scheduledServices.calendar.filters);
  const { areas, companies, equipmentGroups, equipmentModels, frequencies, places, serviceTypes, users } = filters;

  const fetchCalendarEvents = useCallback(() => dispatch(actions.scheduledServicesFetchCalendarEvents(calendarDate.toISOString(), filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies)), [calendarDate, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies]);

  useEffect(() => {
    fetchCalendarEvents();
  }, [calendarDate, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies])

  const onSelectEvent = (event: ICalendarEvent) => {
    dispatch(actions.scheduledServicesSelectCalendarEvent(event.start.toISOString(), event.completed, event.borderColor, event.title));
    setModalOpen(true);
  };

  const onNavigate = (date: Date) => {
    setCalendarDate(date);
  };

  return (<Box position={'relative'}>
    {loading && <Spinner overlay size={40} />}
    <Calendar
      selectable
      popup
      localizer={localizer}
      events={calendarEvents}
      step={30}
      view={view}
      views={['month']}
      date={calendarDate}
      onView={(view) => setView(view)}
      culture={i18n.t('calendarStyledCompCalendar.culture')}
      onSelectEvent={onSelectEvent}
      onNavigate={onNavigate}
      messages={messages}
      style={{ height: '700px'}}
      components={{
        event: CalendarEvent,
      }}
    />
    <Drawer
      variant="temporary"
      anchor="right"
      onClose={() => setModalOpen(false)}
      open={modalOpen}>
        <ScheduledServicesByDayAndCompletedModal closeModal={() => setModalOpen(false)} redirect={redirect}/>
    </Drawer>
  </Box>);
};

export default ScheduledServicesCalendarCalendar;
